import { IProjectRole } from "@kuika/kuika-cl-model";
import React from "react";
import { FolderItem } from "./folder-item";
import { EditingState } from "./role-management-modal";

interface FolderTreeProps {
  folders: any[];
  level?: number;
  search: string;
  roles: IProjectRole[];
  expandedIds: string[];
  editing: EditingState | null;
  screens: any[];
  onToggleExpand: (id: string) => void;
  onAddRole: (folderId: string) => void;
  onAddSubFolder: (parentFolderId: string) => void;
  onEditFolder: (folder: any) => void;
  onDeleteFolder: (folder: any) => void;
  onStartEditRole: (role: IProjectRole) => void;
  onConfirmEditRole: (role: IProjectRole) => void;
  onCancelEdit: () => void;
  onUpdateField: (field: keyof EditingState, value: string) => void;
  onDeleteRole: (role: IProjectRole) => void;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  level = 0,
  search,
  roles,
  expandedIds,
  editing,
  screens,
  onToggleExpand,
  onAddRole,
  onAddSubFolder,
  onEditFolder,
  onDeleteFolder,
  onStartEditRole,
  onConfirmEditRole,
  onCancelEdit,
  onUpdateField,
  onDeleteRole
}) => {
  // Helper function to check if folder or its children contain matching roles
  const folderContainsMatchingRoles = (folder: any): boolean => {
    if (!search) return true;

    // Check if folder name matches
    if (folder.name?.toLowerCase().includes(search.toLowerCase())) {
      return true;
    }

    // Check if any roles in this folder match
    const folderRoles = (folder.roleIds || []).map((rid: string) => roles.find((r) => r.id === rid)).filter(Boolean);

    if (folderRoles.some((role: any) => role.name.toLowerCase().includes(search.toLowerCase()))) {
      return true;
    }

    // Check if any child folders contain matching roles
    if (folder.children && folder.children.length > 0) {
      return folder.children.some((child: any) => folderContainsMatchingRoles(child));
    }

    return false;
  };

  const filteredFolders = folders.filter(folderContainsMatchingRoles).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      {filteredFolders.map((folder) => (
        <React.Fragment key={folder.id}>
          <FolderItem
            folder={folder}
            level={level}
            search={search}
            roles={roles}
            expandedIds={expandedIds}
            editing={editing}
            screens={screens}
            onToggleExpand={onToggleExpand}
            onAddRole={onAddRole}
            onAddSubFolder={onAddSubFolder}
            onEditFolder={onEditFolder}
            onDeleteFolder={onDeleteFolder}
            onStartEditRole={onStartEditRole}
            onConfirmEditRole={onConfirmEditRole}
            onCancelEdit={onCancelEdit}
            onUpdateField={onUpdateField}
            onDeleteRole={onDeleteRole}
          />
          {folder.children && folder.children.length > 0 && (
            <FolderTree
              folders={folder.children}
              level={level + 1}
              search={search}
              roles={roles}
              expandedIds={expandedIds}
              editing={editing}
              screens={screens}
              onToggleExpand={onToggleExpand}
              onAddRole={onAddRole}
              onAddSubFolder={onAddSubFolder}
              onEditFolder={onEditFolder}
              onDeleteFolder={onDeleteFolder}
              onStartEditRole={onStartEditRole}
              onConfirmEditRole={onConfirmEditRole}
              onCancelEdit={onCancelEdit}
              onUpdateField={onUpdateField}
              onDeleteRole={onDeleteRole}
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
};
